import { useScienceTutorSection } from './scienceTutorContext';
import { CheckCircle2, Lightbulb, RefreshCw, ShieldAlert } from 'lucide-react';
import { useId, useState } from 'react';

import {
  BATTERY_CASES,
  BATTERY_CATEGORIES,
  BATTERY_CATEGORY_LABELS,
  BATTERY_CONDITIONS,
  BATTERY_SOURCES,
  ELECTRODE_SIGN_LABELS,
  UNSUPPORTED_BATTERIES,
  batteryFullText,
  batteryHalfText,
  electronFlow,
  evaluateBatteryPrediction,
  findBatteryCase,
  type BatteryCase,
  type ElectrodeSign,
} from './electrochemicalCellsBatteries';

/** Schematic geometry (SVG user units). Every shape is placed from model state, not artwork. */
const VIEW_WIDTH = 640;
const VIEW_HEIGHT = 320;
const ROLE_LABEL_Y = 304;
const CASE_LEFT = 110;
const CASE_RIGHT = 530;
const CASE_TOP = 130;
const CASE_BOTTOM = 280;
const ELECTRODE_WIDTH = 34;
const ELECTRODE_TOP = 110;
const ELECTRODE_HEIGHT = 150;
const NEGATIVE_X = 190;
const POSITIVE_X = 450;
const WIRE_Y = 50;
const DEVICE_WIDTH = 220;
const DEVICE_HEIGHT = 44;
const DEVICE_CENTRE = VIEW_WIDTH / 2;

const ELECTRODE_X: Record<ElectrodeSign, number> = { negative: NEGATIVE_X, positive: POSITIVE_X };
const ELECTRODE_SYMBOL: Record<ElectrodeSign, string> = { negative: '−', positive: '+' };
const DIRECTION_LABEL = { discharge: 'discharging', recharge: 'recharging' } as const;

function BatterySchematic({ item, revealed }: { item: BatteryCase; revealed: boolean }) {
  const markerId = useId();
  const flow = electronFlow(item);
  const fromX = ELECTRODE_X[flow.from];
  const toX = ELECTRODE_X[flow.to];
  const deviceLeft = DEVICE_CENTRE - DEVICE_WIDTH / 2;
  const deviceRight = DEVICE_CENTRE + DEVICE_WIDTH / 2;
  const fromSideX = fromX < toX ? deviceLeft : deviceRight;
  const toSideX = fromX < toX ? deviceRight : deviceLeft;
  const roleOf = (sign: ElectrodeSign) =>
    !revealed ? 'Anode or cathode?' : sign === item.anodeElectrode ? 'Anode · oxidation' : 'Cathode · reduction';

  return (
    <svg
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      className="h-auto w-full"
      role="img"
      aria-label={
        revealed
          ? `${item.name}: ${flow.description}`
          : `${item.name}: negative and positive electrodes joined through ${item.externalDevice.toLowerCase()}. Predict which electrode is the anode.`
      }
    >
      <defs>
        <marker id={markerId} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0 0 L10 5 L0 10 z" className="fill-brand-coral" />
        </marker>
      </defs>
      <rect
        x={CASE_LEFT}
        y={CASE_TOP}
        width={CASE_RIGHT - CASE_LEFT}
        height={CASE_BOTTOM - CASE_TOP}
        rx="16"
        className="fill-wash-sky stroke-ink"
        strokeWidth="3"
      />
      <text x={DEVICE_CENTRE} y={CASE_BOTTOM - 14} textAnchor="middle" className="fill-slate2 text-[13px] font-bold">
        Electrolyte
      </text>
      {(['negative', 'positive'] as ElectrodeSign[]).map((sign) => (
        <g key={sign}>
          <line
            x1={ELECTRODE_X[sign]}
            y1={ELECTRODE_TOP}
            x2={ELECTRODE_X[sign]}
            y2={WIRE_Y}
            className="stroke-ink"
            strokeWidth="3"
          />
          <rect
            x={ELECTRODE_X[sign] - ELECTRODE_WIDTH / 2}
            y={ELECTRODE_TOP}
            width={ELECTRODE_WIDTH}
            height={ELECTRODE_HEIGHT}
            rx="4"
            className={sign === 'negative' ? 'fill-slate2 stroke-ink' : 'fill-surface stroke-ink'}
            strokeWidth="2"
          />
          <text
            x={ELECTRODE_X[sign] + (sign === 'negative' ? -34 : 34)}
            y={ELECTRODE_TOP + 22}
            textAnchor="middle"
            className="fill-ink text-[26px] font-extrabold"
          >
            {ELECTRODE_SYMBOL[sign]}
          </text>
          <text
            x={ELECTRODE_X[sign]}
            y={ROLE_LABEL_Y}
            textAnchor="middle"
            className={`text-[14px] font-extrabold ${revealed && sign === item.anodeElectrode ? 'fill-brand-coral' : 'fill-ink'}`}
          >
            {roleOf(sign)}
          </text>
        </g>
      ))}
      <line x1={NEGATIVE_X} y1={WIRE_Y} x2={deviceLeft} y2={WIRE_Y} className="stroke-ink" strokeWidth="3" />
      <line x1={deviceRight} y1={WIRE_Y} x2={POSITIVE_X} y2={WIRE_Y} className="stroke-ink" strokeWidth="3" />
      <rect
        x={deviceLeft}
        y={WIRE_Y - DEVICE_HEIGHT / 2}
        width={DEVICE_WIDTH}
        height={DEVICE_HEIGHT}
        rx="12"
        className={item.direction === 'recharge' ? 'fill-wash-sunshine stroke-ink' : 'fill-wash-mint stroke-ink'}
        strokeWidth="2"
      />
      <text x={DEVICE_CENTRE} y={WIRE_Y + 5} textAnchor="middle" className="fill-ink text-[15px] font-extrabold">
        {item.externalDevice}
      </text>
      {revealed ? (
        <g>
          <line
            x1={fromX}
            y1={WIRE_Y - 16}
            x2={fromSideX}
            y2={WIRE_Y - 16}
            className="stroke-brand-coral"
            strokeWidth="3"
            markerEnd={`url(#${markerId})`}
          />
          <line
            x1={toSideX}
            y1={WIRE_Y - 16}
            x2={toX}
            y2={WIRE_Y - 16}
            className="stroke-brand-coral"
            strokeWidth="3"
            markerEnd={`url(#${markerId})`}
          />
          <text x={(fromX + fromSideX) / 2} y={WIRE_Y - 24} textAnchor="middle" className="fill-brand-coral text-[14px] font-extrabold">
            e⁻
          </text>
          <text x={(toX + toSideX) / 2} y={WIRE_Y - 24} textAnchor="middle" className="fill-brand-coral text-[14px] font-extrabold">
            e⁻
          </text>
        </g>
      ) : null}
    </svg>
  );
}

function ChoiceGroup<T extends string>({
  legend,
  options,
  labels,
  value,
  onChange,
}: {
  legend: string;
  options: readonly T[];
  labels: Record<T, string>;
  value: string;
  onChange: (value: T) => void;
}) {
  return (
    <fieldset>
      <legend className="text-sm font-extrabold text-ink">{legend}</legend>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={value === option}
            onClick={() => onChange(option)}
            className={`rounded-full border px-4 py-2 text-sm font-extrabold ${value === option ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'}`}
          >
            {labels[option]}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function ResultCard({ correct, title, message }: { correct: boolean; title: string; message: string }) {
  return (
    <div
      className={`rounded-2xl border p-4 ${correct ? 'border-brand-mint/35 bg-wash-mint' : 'border-brand-coral/30 bg-wash-coral'}`}
    >
      <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wide text-ink-soft">
        {correct ? (
          <CheckCircle2 className="h-4 w-4 text-brand-mint" aria-hidden="true" />
        ) : (
          <Lightbulb className="h-4 w-4 text-brand-coral" aria-hidden="true" />
        )}
        {title} · {correct ? 'matches' : 'check this'}
      </p>
      <p className="mt-2 break-words text-sm font-medium leading-6 text-slate2">{message}</p>
    </div>
  );
}

export function BatteriesMode() {
  const [caseId, setCaseId] = useState(BATTERY_CASES[0].id);
  const [anodeChoice, setAnodeChoice] = useState('');
  const [categoryChoice, setCategoryChoice] = useState('');
  const [checked, setChecked] = useState(false);
  useScienceTutorSection('BatteriesMode', { caseId, anodeChoice, categoryChoice, checked });
  const lookup = findBatteryCase(caseId);
  const item = lookup.status === 'ok' ? lookup.item : null;
  const result = item && checked ? evaluateBatteryPrediction(item, anodeChoice, categoryChoice) : null;
  const feedback = result?.status === 'checked' ? result : null;

  const choose = (id: string) => {
    setCaseId(id);
    setAnodeChoice('');
    setCategoryChoice('');
    setChecked(false);
  };

  return (
    <section className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
      <aside className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
          Reviewed cells
        </p>
        <div className="mt-3 grid gap-2">
          {BATTERY_CASES.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-pressed={entry.id === caseId}
              onClick={() => choose(entry.id)}
              className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold text-ink ${entry.id === caseId ? 'border-brand-sky bg-wash-sky' : 'border-hairline bg-surface hover:bg-wash-sky'}`}
            >
              {entry.name}
            </button>
          ))}
        </div>
        <p className="mt-5 text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          Not modelled yet
        </p>
        <div className="mt-3 grid gap-2">
          {UNSUPPORTED_BATTERIES.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-pressed={entry.id === caseId}
              onClick={() => choose(entry.id)}
              className={`rounded-2xl border border-dashed px-4 py-3 text-left text-sm font-extrabold text-slate2 ${entry.id === caseId ? 'border-brand-coral bg-wash-coral' : 'border-hairline bg-surface'}`}
            >
              {entry.name}
            </button>
          ))}
        </div>
      </aside>

      <div className="min-w-0 space-y-6">
        <section className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
          {item ? (
            <>
              <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
                {DIRECTION_LABEL[item.direction]} · {item.everydayUse}
              </p>
              <h2 className="mt-2 text-2xl font-extrabold text-ink">{item.name}</h2>
              <div className="mt-5 rounded-[1.75rem] border border-brand-sky/20 bg-surface p-3">
                <BatterySchematic item={item} revealed={checked && feedback !== null} />
              </div>
              <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
                {[
                  { term: ELECTRODE_SIGN_LABELS.negative, value: item.negativeMaterial },
                  { term: 'Electrolyte', value: item.electrolyte },
                  { term: ELECTRODE_SIGN_LABELS.positive, value: item.positiveMaterial },
                ].map((entry) => (
                  <div key={entry.term} className="rounded-2xl bg-surface p-3">
                    <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">{entry.term}</dt>
                    <dd className="mt-1 font-bold text-ink">{entry.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6 grid gap-4">
                <ChoiceGroup
                  legend={`While the cell is ${DIRECTION_LABEL[item.direction]}, which electrode is the anode (oxidation)?`}
                  options={['negative', 'positive'] as const}
                  labels={ELECTRODE_SIGN_LABELS}
                  value={anodeChoice}
                  onChange={(value) => {
                    setAnodeChoice(value);
                    setChecked(false);
                  }}
                />
                <ChoiceGroup
                  legend="What type of cell is this?"
                  options={BATTERY_CATEGORIES}
                  labels={BATTERY_CATEGORY_LABELS}
                  value={categoryChoice}
                  onChange={(value) => {
                    setCategoryChoice(value);
                    setChecked(false);
                  }}
                />
              </div>
              <button
                type="button"
                disabled={!anodeChoice || !categoryChoice}
                onClick={() => setChecked(true)}
                className="btn-pill-primary mt-5 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Run the cell
              </button>

              {result?.status === 'invalid' ? (
                <p role="status" className="mt-4 text-sm font-bold text-ink">
                  {result.reason}
                </p>
              ) : null}

              {feedback ? (
                <div role="status" className="mt-6 space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ResultCard correct={feedback.anodeCorrect} title="Anode" message={feedback.anodeMessage} />
                    <ResultCard correct={feedback.categoryCorrect} title="Cell type" message={feedback.categoryMessage} />
                  </div>
                  <div className="rounded-2xl border border-hairline bg-surface p-4 text-sm">
                    <p className="font-extrabold text-ink">
                      Anode ({item.anodeElectrode === 'negative' ? '−' : '+'}), oxidation
                    </p>
                    <p className="mt-1 break-words font-bold text-slate2">{batteryHalfText(item.anode)}</p>
                    <p className="mt-3 font-extrabold text-ink">
                      Cathode ({item.anodeElectrode === 'negative' ? '+' : '−'}), reduction
                    </p>
                    <p className="mt-1 break-words font-bold text-slate2">{batteryHalfText(item.cathode)}</p>
                    <p className="mt-3 font-extrabold text-ink">Overall while {DIRECTION_LABEL[item.direction]}</p>
                    <p className="mt-1 break-words font-bold text-slate2">{batteryFullText(item.overall)}</p>
                  </div>
                  <ul className="grid gap-2 text-sm font-medium leading-6 text-slate2">
                    <li>
                      <strong className="text-ink">Electron flow:</strong> {electronFlow(item).description}
                    </li>
                    <li>
                      <strong className="text-ink">Inside the cell:</strong> {item.ionFlow}
                    </li>
                    <li>
                      <strong className="text-ink">Energy change:</strong>{' '}
                      {item.energyChange === 'chemical-to-electrical'
                        ? 'chemical energy → electrical energy'
                        : 'electrical energy → chemical energy'}
                    </li>
                    <li>
                      <strong className="text-ink">Voltage:</strong> {item.voltage.label}
                    </li>
                  </ul>
                  {item.pairedCaseId ? (
                    <button
                      type="button"
                      onClick={() => choose(item.pairedCaseId ?? item.id)}
                      className="inline-flex items-center gap-2 rounded-full border border-hairline px-4 py-2 text-sm font-extrabold text-ink"
                    >
                      <RefreshCw className="h-4 w-4" aria-hidden="true" />
                      Show the same cell {item.direction === 'discharge' ? 'recharging' : 'discharging'}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : (
            <div role="status" className="flex items-start gap-3 rounded-2xl border border-brand-coral/30 bg-wash-coral p-5">
              <ShieldAlert className="mt-0.5 h-6 w-6 shrink-0 text-brand-coral" aria-hidden="true" />
              <div>
                <h2 className="font-extrabold text-ink">Not in the reviewed cell list</h2>
                <p className="mt-1 text-sm font-medium leading-6 text-slate2">
                  {lookup.status === 'unsupported' ? lookup.reason : null} The tool does not guess its
                  half-equations.
                </p>
              </div>
            </div>
          )}
        </section>

        <section className="rounded-3xl border border-hairline bg-surface p-5 text-sm font-medium leading-6 text-slate2">
          <strong className="text-ink">Model limit:</strong> {BATTERY_CONDITIONS} Real cells also
          lose voltage as they run down and age. Never open, puncture or recharge a primary cell.
          <span className="mt-2 block text-xs font-bold">
            Sources: {Object.values(BATTERY_SOURCES).map((source) => source.citation).join(' · ')}
          </span>
        </section>
      </div>
    </section>
  );
}
